import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import WaitlistDialog from "../components/WaitlistDialog";

/**
 * Centralised waitlist CTA behaviour.
 *
 * - On the homepage ("/"), `openWaitlist` smooth-scrolls to the form
 *   section and focuses the first input.
 * - On every other route it opens a centred dialog that hosts the same
 *   WaitlistForm component used inline on the homepage.
 *
 * Wrap the app with <WaitlistCTAProvider> once at the Layout level. Any
 * button on any page can call `useWaitlistCTA().openWaitlist(e)` and get
 * the right behaviour for the current route.
 */
const HOME_PATH = "/";

/**
 * Smooth-scrolls to the waitlist form on the homepage and focuses the
 * first input.
 *
 * Implementation note: the page below the hero contains lazy-loaded
 * images. A single smooth scroll often misses the target because images
 * load mid-flight, growing the document and leaving the form below the
 * viewport. We use a two-pass approach: scroll, wait for the smooth
 * animation to settle and lazy images to resolve, then re-target with
 * an instant scroll to correct any layout drift. The section already
 * has `scroll-mt-24` so `block: 'start'` lands cleanly below the header.
 */
const scrollToWaitlistOnHome = () => {
    const section = document.getElementById("waitlist");
    const input = document.getElementById("tp-waitlist-name");
    if (!section && !input) {
        window.location.hash = "#waitlist";
        return;
    }
    const target = section || input;
    target.scrollIntoView({ behavior: "smooth", block: "start" });

    // After the smooth scroll has had time to complete and lazy assets
    // have likely resolved, re-snap to the section in case the document
    // grew under our feet. Instant scroll avoids a second visible jump.
    const correct = () => {
        const fresh = document.getElementById("waitlist") || document.getElementById("tp-waitlist-name");
        if (!fresh) return;
        const rect = fresh.getBoundingClientRect();
        // If the section is already comfortably in view, skip the correction.
        if (rect.top >= 60 && rect.top <= 240) return;
        fresh.scrollIntoView({ behavior: "auto", block: "start" });
    };
    setTimeout(correct, 750);
    setTimeout(() => {
        correct();
        const focusEl = document.getElementById("tp-waitlist-name");
        if (focusEl && typeof focusEl.focus === "function") {
            focusEl.focus({ preventScroll: true });
        }
    }, 1100);
};

const WaitlistCTAContext = createContext({
    openWaitlist: () => {},
});

export const WaitlistCTAProvider = ({ children }) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);

    const openWaitlist = useCallback(
        (e) => {
            if (e && typeof e.preventDefault === "function") e.preventDefault();
            if (pathname === HOME_PATH) {
                scrollToWaitlistOnHome();
                return;
            }
            setDialogOpen(true);
        },
        [pathname],
    );

    const closeDialog = useCallback(() => setDialogOpen(false), []);

    // If the route changes for any reason while the dialog is open, close
    // it. KickoffLabs redirects to /thank-you after a successful submit,
    // so this also handles the post-submit transition cleanly.
    useEffect(() => {
        if (dialogOpen) setDialogOpen(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    // Helper for places like the Header that want a brand link that
    // jumps to the homepage form regardless of current route.
    const goHomeToWaitlist = useCallback(() => {
        if (pathname === HOME_PATH) {
            scrollToWaitlistOnHome();
        } else {
            navigate("/#waitlist");
        }
    }, [navigate, pathname]);

    const value = useMemo(
        () => ({ openWaitlist, goHomeToWaitlist }),
        [openWaitlist, goHomeToWaitlist],
    );

    return (
        <WaitlistCTAContext.Provider value={value}>
            {children}
            <WaitlistDialog open={dialogOpen} onClose={closeDialog} />
        </WaitlistCTAContext.Provider>
    );
};

export const useWaitlistCTA = () => useContext(WaitlistCTAContext);
